<template>
  <v-dialog v-model="dialog" max-width="800px">
    <template v-slot:activator="{ on }">
      <v-list-item v-on="on">
        <v-list-item-title>Exportar arquivo</v-list-item-title>
      </v-list-item>
    </template>
    <v-card>
      <v-card-title>
        <span class="title">Exportar ofertas</span>
      </v-card-title>
      <v-card-text>
        <v-expansion-panels flat multiple>
          <v-expansion-panel
            v-for="(dataType, indexDataType) in dataTypes"
            :key="indexDataType"
            class="my-0"
            :readonly="dataType.keyName === 'combo_offer_sales' || dataType.keyName === 'dynamic_rule_offer_sales'"
          >
            <v-expansion-panel-header class="pa-0" @click="toggleExpansionPanel(dataType, indexDataType)">
              <v-row>
                <v-col class="d-flex py-0 align-center">
                  <v-checkbox
                    v-model="dataType.selected"
                    color="primary"
                    :label="dataType.label"
                    :disabled="checkIfDataTypeIsDisabled(dataType.keyName)"
                    @click="toggleExpansionPanel(dataType, indexDataType)"
                  />

                  <v-tooltip v-if="dataType.keyName === 'combo_offer_sales' || dataType.keyName === 'dynamic_rule_offer_sales'" right max-width="250">
                    <template v-slot:activator="{ on, attrs }">
                      <span v-bind="attrs" v-on="on">
                        <mf-icon fill color="#ffc107" small class="pl-1">warning</mf-icon>
                      </span>
                    </template>
                    <span>Atualmente esse tipo de oferta está indisponível para exportar.</span>
                  </v-tooltip>

                  <v-tooltip v-else-if="checkIfOfferSalesHasProducts(dataType.keyName)" right max-width="250">
                    <template v-slot:activator="{ on, attrs }">
                      <span v-bind="attrs" v-on="on">
                        <mf-icon color="cinza-0" small class="pl-1">info</mf-icon>
                      </span>
                    </template>
                    <span>Tipos de oferta sem produtos são desativados automaticamente para evitar problemas ao tentar exportar.</span>
                  </v-tooltip>
                </v-col>
              </v-row>
            </v-expansion-panel-header>
            <v-expansion-panel-content>
              <p v-for="(dataTypeProperty, indexDataTypeProperty) in dataType.headers" :key="indexDataTypeProperty" class="mf-body-2 pl-1">
                <mf-icon small>chevron_right</mf-icon>
                {{ dataTypeProperty.friendlyName }}
              </p>
            </v-expansion-panel-content>
          </v-expansion-panel>
        </v-expansion-panels>
      </v-card-text>
      <v-card-actions class="flex-row-reverse mx-4">
        <export-data :data-fetcher="getDataToExport" :filename="exportFileName" :disabled="dataTypesSelected.length === 0 ? true : false" />
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import { assertArray } from '@/config/helpers.js'
import { QUERY_SALES_CAMPAIGN_BY_ID } from '../../graphql'
import ExportData from '@/components/export-data/ExportData.vue'

export default {
  name: 'ExportOfferSalesModal',
  components: { ExportData },
  props: {
    offerSalesId: {
      type: String,
      required: true,
    },
    isIndustryCampaign: {
      type: Boolean,
      default: false,
    },
    isMercapromoCampaign: {
      type: Boolean,
      default: false,
    },
    isFranchisee: {
      type: Boolean,
      default: false,
    },
  },
  data: () => ({
    dialog: false,
    loadingOfferSales: false,
    exportFileName: '',
    offers: {
      campaignName: '',
      allOfferSales: {
        offer_sales: [],
        percentage_offer_sales: [],
        dynamic_pricing_percentage_offer_sales: [],
        takepay_offer_sales: [],
        combo_offer_sales: [],
        cashback_percentage_offer_sales: [],
        cashback_absolute_offer_sales: [],
        points_offer_sales: [],
        next_buy_cashback_offer_sales: [],
        dynamic_rule_offer_sales: [],
      },
    },
    dataTypes: [],
  }),
  computed: {
    isAdvancedOffer() {
      return this.$getAllowedModules()['advanced_discounts']
    },
    isOffersByUnit() {
      return this.$getAllowedModules()['offers_by_unit']
    },
    isCashbackAbsolute() {
      return this.$getAllowedModules()['cashback']
    },
    isCashbackPercentage() {
      return this.$getAllowedModules()['cashback']
    },
    isPoints() {
      return this.$getAllowedModules()['points']
    },
    modules() {
      return this.$getAllowedModules()
    },
    dataTypesSelected() {
      return this.dataTypes.filter(({ selected }) => selected)
    },
    hasNoDataTypesSelected() {
      return !this.dataTypesSelected.length
    },
  },
  watch: {
    dialog(value) {
      if (!value) return
      else this.updateOffers()
      this.$emit('modal-open')
    },
  },
  mounted() {
    this.initDataTypes()
  },
  methods: {
    initDataTypes() {
      const defaultHeaders = [
        {
          csvName: 'sku',
          friendlyName: 'SKU',
        },
        {
          csvName: 'nome',
          friendlyName: 'Nome',
        },
      ]

      const dataTypes = [
        {
          selected: false,
          label: 'Tipo de oferta absoluto',
          keyName: 'offer_sales',
          headers: [
            ...defaultHeaders,
            {
              csvName: 'preço 1',
              friendlyName: 'Preço 1',
            },
            {
              csvName: 'preço 2',
              friendlyName: 'Preço 2',
            },
            {
              csvName: 'mínimo por cliente',
              friendlyName: 'Mínimo por cliente',
            },
            {
              csvName: 'máximo por cliente',
              friendlyName: 'Máximo por cliente',
            },
          ],
          extraFieldsKeyName: ['price_1', 'price_2', 'minimum_per_client', 'maximum_per_client'],
        },
      ]

      if (this.isIndustryCampaign) {
        dataTypes[0].headers.push({
          csvName: 'reembolso',
          friendlyName: 'Reembolso',
        })
        dataTypes[0].extraFieldsKeyName.push('rebate')
      }

      if (this.isAdvancedOffer) {
        const advancedOffersDataTypes = [
          {
            selected: false,
            label: 'Tipo de oferta percentual',
            keyName: 'percentage_offer_sales',
            headers: [
              ...defaultHeaders,
              {
                csvName: 'desconto',
                friendlyName: 'Desconto',
              },
              {
                csvName: 'mínimo por cliente',
                friendlyName: 'Mínimo por cliente',
              },
              {
                csvName: 'máximo por cliente',
                friendlyName: 'Máximo por cliente',
              },
            ],
            extraFieldsKeyName: ['percentage_discount', 'minimum_per_client', 'maximum_per_client'],
          },
          {
            selected: false,
            label: 'Tipo de oferta leve e pague',
            keyName: 'takepay_offer_sales',
            headers: [
              ...defaultHeaders,
              {
                csvName: 'leve',
                friendlyName: 'Leve',
              },
              {
                csvName: 'pague',
                friendlyName: 'Pague',
              },
              {
                csvName: 'máximo de grupos por cpf',
                friendlyName: 'Máximo de grupos por CPF',
              },
            ],
            extraFieldsKeyName: ['take', 'pay', 'maximum_per_client'],
          },
          {
            selected: false,
            label: 'Tipo de oferta combo',
            keyName: 'combo_offer_sales',
            headers: [],
            extraFieldsKeyName: [],
          },
        ]

        dataTypes.push(...advancedOffersDataTypes)
      }

      if (this.isOffersByUnit) {
        const offersByUnitDataType = {
          selected: false,
          label: 'Tipo de oferta percentual por loja',
          keyName: 'dynamic_pricing_percentage_offer_sales',
          headers: [
            ...defaultHeaders,
            {
              csvName: 'desconto',
              friendlyName: 'Desconto',
            },
            {
              csvName: 'mínimo por cliente',
              friendlyName: 'Mínimo por cliente',
            },
            {
              csvName: 'máximo por cliente',
              friendlyName: 'Máximo por cliente',
            },
          ],
          extraFieldsKeyName: ['percentage_discount', 'minimum_per_client', 'maximum_per_client'],
        }
        dataTypes.push(offersByUnitDataType)
      }

      if (this.isCashbackPercentage) {
        const cashbackPercentageDataType = {
          selected: false,
          label: 'Tipo de oferta cashback percentual',
          keyName: 'cashback_percentage_offer_sales',
          headers: [
            ...defaultHeaders,
            {
              csvName: 'preço 1',
              friendlyName: 'Preço 1',
            },
            {
              csvName: 'cashback %',
              friendlyName: 'Cashback percentual',
            },
            {
              csvName: 'mínimo por cliente',
              friendlyName: 'Mínimo por cliente',
            },
            {
              csvName: 'máximo por cliente',
              friendlyName: 'Máximo por cliente',
            },
          ],
          extraFieldsKeyName: ['price_1', 'cashback_percentage', 'minimum_per_client', 'maximum_per_client'],
        }
        dataTypes.push(cashbackPercentageDataType)
      }
      if (this.isCashbackAbsolute) {
        const cashbackAbsoluteDataType = {
          selected: false,
          label: 'Tipo de oferta cashback absoluto  ',
          keyName: 'cashback_absolute_offer_sales',
          headers: [
            ...defaultHeaders,
            {
              csvName: 'preço',
              friendlyName: 'Preço',
            },
            {
              csvName: 'cashback absoluto',
              friendlyName: 'Cashback absoluto',
            },
            {
              csvName: 'mínimo por cliente',
              friendlyName: 'Mínimo por cliente',
            },
            {
              csvName: 'máximo por cliente',
              friendlyName: 'Máximo por cliente',
            },
          ],
          extraFieldsKeyName: ['price', 'cashback_absolute', 'minimum_per_client', 'maximum_per_client'],
        }

        dataTypes.push(cashbackAbsoluteDataType)
      }

      if (this.isPoints) {
        const pointsDataType = {
          selected: false,
          label: 'Tipo de oferta pontos',
          keyName: 'points_offer_sales',
          headers: [
            ...defaultHeaders,
            {
              csvName: 'preço',
              friendlyName: 'Preço',
            },
            {
              csvName: 'pontos',
              friendlyName: 'Pontos',
            },
            {
              csvName: 'mínimo por cliente',
              friendlyName: 'Mínimo por cliente',
            },
            {
              csvName: 'máximo por cliente',
              friendlyName: 'Máximo por cliente',
            },
          ],
          extraFieldsKeyName: ['price', 'additional_points_rate', 'minimum_per_client', 'maximum_per_client'],
        }
        dataTypes.push(pointsDataType)
      }

      if (this.isMercapromoCampaign) {
        const mercapromoTypes = [
          {
            selected: false,
            label: 'Tipo de oferta cashback próxima compra',
            keyName: 'next_buy_cashback_offer_sales',
            headers: [
              ...defaultHeaders,
              {
                csvName: 'cashback',
                friendlyName: 'Cashback',
              },
              {
                csvName: 'mínimo de unidades',
                friendlyName: 'Mínimo de unidades',
              },
              {
                csvName: 'máximo por cpf',
                friendlyName: 'Máximo por CPF',
              },
            ],
            extraFieldsKeyName: ['cashback_absolute_discount', 'minimum_per_client', 'max_coupons'],
          },
          {
            selected: false,
            label: 'Tipo de oferta compre e ganhe',
            keyName: 'dynamic_rule_offer_sales',
            headers: [],
            extraFieldsKeyName: [],
          },
        ]
        dataTypes.push(...mercapromoTypes)
      }

      this.dataTypes = dataTypes
    },
    mountAndReturnExtraFieldsObject(extraFieldsKeyName, item) {
      return extraFieldsKeyName.reduce((allExtraFields, fieldKeyName) => {
        allExtraFields = { ...allExtraFields, [fieldKeyName]: item[fieldKeyName] }
        return allExtraFields
      }, {})
    },
    mountAndReturnAllProductsList(offerSales, offerSalesKeyName, extraFieldsKeyName = []) {
      return offerSales.reduce((allProducts, offer) => {
        const { sku, name } = offer

        const productsGroup = offer[offerSalesKeyName]

        if (productsGroup) {
          const formattedProductsGroup = productsGroup.map((groupProduct) => {
            const extraFields = this.mountAndReturnExtraFieldsObject(extraFieldsKeyName, offer)

            return {
              sku: groupProduct.sku,
              name: groupProduct.name,
              ...extraFields,
            }
          })

          allProducts.push(...formattedProductsGroup)
        } else {
          const extraFields = this.mountAndReturnExtraFieldsObject(extraFieldsKeyName, offer)
          const formattedProduct = {
            sku,
            name,
            ...extraFields,
          }

          allProducts.push(formattedProduct)
        }

        return allProducts
      }, [])
    },
    async updateOffers() {
      try {
        this.loadingOfferSales = true
        const {
          data: { offerSchedule },
        } = await this.$apollo.query({
          query: QUERY_SALES_CAMPAIGN_BY_ID,
          variables: {
            id: this.offerSalesId,
            franchise: this.isFranchisee,
          },
          context: {
            uri: this.$microservicesUrls['sales'],
          },
          fetchPolicy: 'no-cache',
        })
        this.offers = {
          campaignName: offerSchedule.name,
          allOfferSales: {
            offer_sales: offerSchedule?.offer_sales || [],
            percentage_offer_sales: offerSchedule?.percentage_offer_sales || [],
            dynamic_pricing_percentage_offer_sales: offerSchedule?.dynamic_pricing_percentage_offer_sales || [],
            takepay_offer_sales: offerSchedule?.takepay_offer_sales || [],
            combo_offer_sales: offerSchedule?.combo_offer_sales || [],
            cashback_percentage_offer_sales: offerSchedule?.cashback_percentage_offer_sales || [],
            cashback_absolute_offer_sales: offerSchedule?.cashback_absolute_offer_sales || [],
            points_offer_sales: offerSchedule?.points_offer_sales || [],
            next_buy_cashback_offer_sales: offerSchedule?.next_buy_cashback_offer_sales || [],
            dynamic_rule_offer_sales: offerSchedule?.dynamic_rule_offer_sales || [],
          },
        }
      } catch (error) {
        this.$snackbar({ message: 'Erro ao tentar buscar ofertas', snackbarColor: 'error' })
      } finally {
        this.loadingOfferSales = false
      }
    },
    async getDataToExport() {
      try {
        const offerSalesSelectedAndWithProducts = Object.entries(this.offers.allOfferSales).filter(
          ([offerSalesKeyName, offerSales]) =>
            offerSales.length && offerSalesKeyName !== 'combo_offer_sales' && this.dataTypesSelected.find(({ keyName }) => keyName === offerSalesKeyName)
        )

        const offerSalesWithHeaders = offerSalesSelectedAndWithProducts.map(([offerSalesKeyName, offerSales]) => {
          const offerSalesSelected = this.dataTypesSelected.find(({ keyName }) => keyName === offerSalesKeyName) ?? {}
          const headers = assertArray(offerSalesSelected?.headers.map(({ csvName }) => csvName))
          const products = this.mountAndReturnAllProductsList(offerSales, offerSalesKeyName, offerSalesSelected?.extraFieldsKeyName)
          const csvFileName = `${this.offers.campaignName} - ${offerSalesSelected?.label}`
          return [offerSalesKeyName, { headers, products, csvFileName }]
        })
        for (const [, offerSales] of offerSalesWithHeaders) {
          this.exportFileName = offerSales.csvFileName
          const rows = offerSales.products.map((product) => {
            return Object.values(product)
          })
          return { headers: offerSales.headers, rows }
        }
      } catch (error) {
        this.$snackbar({ message: 'Houve um erro ao tentar exportar ofertas', snackbarColor: 'error' })
      }
    },
    checkIfOfferSalesHasProducts(offerSalesKeyName) {
      return !this.offers.allOfferSales[offerSalesKeyName].length
    },
    checkIfDataTypeIsDisabled(offerSalesKeyName) {
      return (
        this.loadingOfferSales ||
        this.checkIfOfferSalesHasProducts(offerSalesKeyName) ||
        offerSalesKeyName === 'combo_offer_sales' ||
        offerSalesKeyName === 'dynamic_rule_offer_sales'
      )
    },
    toggleExpansionPanel(dataType, indexDataType) {
      const { selected, keyName } = dataType
      if (!this.checkIfDataTypeIsDisabled(keyName)) {
        this.dataTypes[indexDataType].selected = !selected
      }
    },
  },
}
</script>
